import { useEffect, useState } from "react";
import { History, Loader2, RotateCcw } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui";
import { useFormat } from "@/i18n/format";
import { cn } from "@/lib/utils";

/**
 * Version history for chapters and summaries.
 *
 * Restoring copies a snapshot into the live tables; the snapshot itself is
 * never touched, so "v3" means the same thing next month. The current version
 * is shown but not restorable — restoring what is already live is a no-op
 * wearing a button.
 */
export type VersionItem = {
  id: string;
  label: string;
  detail: string | null;
  createdAt: string;
  isLive: boolean;
};

export function VersionsMenu({
  items,
  loading,
  restoringId,
  onOpen,
  onRestore,
}: {
  items: VersionItem[];
  loading: boolean;
  restoringId: string | null;
  /** Fired when the menu opens, so the list can be fetched lazily. */
  onOpen: () => void;
  onRestore: (id: string) => void;
}) {
  const { t } = useTranslation("recording");
  const f = useFormat();
  const [open, setOpen] = useState(false);
  /**
   * Viewport-clamped position, measured from the button when it opens.
   *
   * A `right-0` card-anchored menu extends left of the screen whenever the
   * button sits within 320px of the left edge — which on a phone is always.
   * Fixed + clamped is the same pattern the transcript's speaker menu uses.
   */
  const [at, setAt] = useState<{ top: number; left: number } | null>(null);
  const MENU_W = 320;

  // Escape closes it, like any menu. The backdrop already handles clicks, but
  // a keyboard user (or anyone whose pointer is mid-drag) needs the key.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <div className="relative">
      <Button
        variant="ghost"
        size="sm"
        onClick={(e) => {
          const next = !open;
          if (next) {
            const rect = e.currentTarget.getBoundingClientRect();
            const width = Math.min(MENU_W, window.innerWidth - 16);
            setAt({
              top: rect.bottom + 4,
              left: Math.max(8, Math.min(rect.right - width, window.innerWidth - width - 8)),
            });
          }
          setOpen(next);
          if (next) onOpen();
        }}
        aria-expanded={open}
      >
        <History size={13} />
        {t("versions.button")}
      </Button>

      {open ? (
        <>
          {/* Click-away layer — cheaper and more reliable than doc listeners. */}
          <button
            type="button"
            aria-label={t("versions.close")}
            className="fixed inset-0 z-40 cursor-default"
            onClick={() => setOpen(false)}
          />
          <div
            className={cn(
              "fixed z-50 w-80 max-w-[calc(100vw-1rem)] rounded-lg border border-border",
              "bg-surface p-1 shadow-[var(--shadow-md)]",
            )}
            style={at ?? undefined}
          >
            {loading ? (
              <p className="flex items-center gap-2 p-3 text-[13px] text-text-muted">
                <Loader2 size={13} className="animate-spin" /> {t("versions.loading")}
              </p>
            ) : items.length === 0 ? (
              <p className="p-3 text-[13px] leading-relaxed text-text-muted">
                {t("versions.empty")}
              </p>
            ) : (
              <div className="max-h-72 overflow-y-auto">
                {items.map((item) => (
                  <div
                    key={item.id}
                    className="flex items-center justify-between gap-2 rounded px-2 py-1.5 hover:bg-surface-hover"
                  >
                    <div className="min-w-0">
                      <p className="truncate text-[13px] text-text">
                        {item.label}
                        {item.isLive ? (
                          <span className="ml-2 rounded bg-accent-subtle px-1.5 py-0.5 text-[10px] font-medium text-accent">
                            {t("versions.current")}
                          </span>
                        ) : null}
                      </p>
                      <p className="text-[11.5px] text-text-faint">
                        {item.detail ? `${item.detail} · ` : ""}
                        {f.dateTime(item.createdAt)}
                      </p>
                    </div>
                    {!item.isLive ? (
                      <button
                        type="button"
                        disabled={restoringId !== null}
                        onClick={() => onRestore(item.id)}
                        className={cn(
                          "flex shrink-0 items-center gap-1 rounded px-2 py-1",
                          "text-[12px] text-text-muted hover:bg-surface hover:text-text",
                        )}
                      >
                        {restoringId === item.id ? (
                          <Loader2 size={11} className="animate-spin" />
                        ) : (
                          <RotateCcw size={11} />
                        )}
                        {t("versions.restore")}
                      </button>
                    ) : null}
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      ) : null}
    </div>
  );
}
